import { QueryClient } from '@tanstack/react-query';
import algoliasearch from 'algoliasearch/lite';
import clsx from 'clsx';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { Configure, InstantSearch } from 'react-instantsearch';
import { useContext } from 'use-context-selector';
import { storeLocale } from '@/root/constants';
import { CacheLong, CacheNone } from '@/root/strategies';
import { FilterAppliedMiddleware } from 'components/algoliaHelpers/FilterAppliedMiddleware';
import { CollectionFilters } from 'components/collectionFilters/CollectionFilters';
import { GlobalContentContext } from 'components/globalContentContext/GlobalContentContext';
import { InstantSearchList } from 'components/InstantSearchList/InstantSearchList';
import type { AlgoliaNailArtHit } from 'components/nailArtCardHit/NailArtCardHit';
import { NailArtCardHit } from 'components/nailArtCardHit/NailArtCardHit';
import { NailArtHeaderConnected } from 'components/nailArtHeader/NailArtHeaderConnected';
import { NailArtLandingContent } from 'components/nailArtLandingContent/NailArtLandingContent';
import { NailArtLandingProvider } from 'components/nailArtLandingProvider/NailArtLandingProvider';
import type { GetNailArtLandingQuery } from 'lib/contentful/__generated__/NailArtLandingQuery';
import { useGetNailArtLandingQuery } from 'lib/contentful/__generated__/NailArtLandingQuery';
import { contentfulQueryDataSource } from 'lib/contentful/dataSources';
import { algoliaNailIndex } from 'utils/algoliaConstants';
import { algoliaRouting } from 'utils/algoliaRouting';
import {
  getAlgoliaContentfulFilters,
  getAlgoliaNailArtUrlCategoryFilter,
} from 'utils/contentfulHelpers';
import type { DefaultProps } from 'utils/defaultsStaticProps';
import { defaultStaticProps } from 'utils/defaultsStaticProps';
import styles from './nailArtLanding.module.scss';

export default function NailArtLanding() {
  const [searchClient] = useState(() =>
    algoliasearch(
      process.env.NEXT_PUBLIC_ALGOLIA_ID ?? '',
      process.env.NEXT_PUBLIC_ALGOLIA_KEY ?? ''
    )
  );

  const { locale: routerLocale } = useRouter();
  const locale = storeLocale(routerLocale);

  const nailArtIndex = algoliaNailIndex(locale);

  /**
   * Set up Algolia routing config
   * - Grabs the filter settings data from Contentful and passes it to the
   *   `algoliaRouting` options.
   * - Uses `useMemo` to prevent resetting the routing object on re-render.
   */
  const { filterSettings } = useContext(GlobalContentContext);
  const nailArtFilterSettings = filterSettings?.nailart;
  const routing = useMemo(
    () =>
      algoliaRouting(
        {
          filterSettings: nailArtFilterSettings,
          index: nailArtIndex,
        },
        locale
      ),
    []
  );

  const [hasFiltersApplied, setHasFiltersApplied] = useState(false);

  const handleAlgoliaRefinementChange = (algoliaFiltersApplied: boolean) => {
    if (algoliaFiltersApplied !== hasFiltersApplied) {
      setHasFiltersApplied(algoliaFiltersApplied);
    }
  };

  const baseFilters = `${getAlgoliaContentfulFilters(
    locale
  )} AND ${getAlgoliaNailArtUrlCategoryFilter(false)}`;

  return (
    <div className={styles.nailArtLanding}>
      <NailArtLandingProvider>
        <InstantSearch
          searchClient={searchClient}
          indexName={nailArtIndex}
          routing={routing}
        >
          <Configure filters={baseFilters} />

          <NailArtHeaderConnected />

          <CollectionFilters
            indexType="nailart"
            filtersType="nailart"
            showSorting={false}
          />

          <FilterAppliedMiddleware
            setAlgoliaRefinement={handleAlgoliaRefinementChange}
            indexName={nailArtIndex}
          />

          <NailArtLandingContent
            nailArtGrid={<NailArtCardGrid />}
            hasFiltersApplied={hasFiltersApplied}
          />
        </InstantSearch>
      </NailArtLandingProvider>
    </div>
  );
}

const NailArtCardGrid = () => {
  return (
    <div className={clsx(styles.nailArtLanding__gridWrapper, 'container')}>
      <InstantSearchList
        hitComponent={({ hit }) => (
          <NailArtCardHit hit={hit as AlgoliaNailArtHit} />
        )}
        itemClassName="col xs2 l3"
        gridClassName={styles.nailArtLanding__grid}
      />
    </div>
  );
};

export const getServerSideProps: GetServerSideProps<DefaultProps> = async (
  context
) => {
  const { res, preview = false } = context;

  if (preview) {
    res.setHeader('Cache-Control', CacheNone());
  } else {
    res.setHeader('Cache-Control', CacheLong());
  }

  const locale = storeLocale(context.locale);

  const variables = {
    locale,
    preview,
  };

  const queryClient = new QueryClient();
  const staticProps = await defaultStaticProps(context, {
    queryClient,
    queries: [
      queryClient.prefetchQuery(
        useGetNailArtLandingQuery.getKey(variables),
        useGetNailArtLandingQuery.fetcher(
          contentfulQueryDataSource({ byPassCache: preview }),
          variables
        )
      ),
    ],
  });

  const nailArtLandingData = queryClient
    .getQueryCache()
    .find<GetNailArtLandingQuery>(useGetNailArtLandingQuery.getKey(variables));

  const tags =
    nailArtLandingData?.state.data?.nailArtLanding?.contentfulMetadata.tags;

  const isCurrentLocaleInTags = tags?.some(
    (tag) => tag?.id?.toLowerCase() === locale.toLowerCase()
  );

  if (!isCurrentLocaleInTags) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      ...staticProps,
    },
  };
};
